import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Table,
  Textarea,
  Typography,
} from '@mui/joy';
import { FaTimes } from 'react-icons/fa';
import { IconWrapper } from '../../components/IconWrapper';
import {
  JOB_REVIEWS_MISSING,
  loadJobReview,
  saveJobReview,
  type JobReviewDetail,
} from '../../services/jobReviews.service';
import type { JobActuals } from '../../types/jobActuals.types';
import {
  formatMoney,
  buildWorkerCosts,
  canMarkReviewed,
  fittingComparison,
  fittingDayRate,
  quotedFittingCost,
  workerDays,
  formatQty,
  groupReviewLines,
  isCompanyFlatRate,
  isInstallDone,
  moneyVarianceLabel,
  prefillActuals,
  qtyVarianceLabel,
  reviewLineCost,
  sourceJobRef,
  summariseReview,
  unitLabel,
  varianceColor,
  type JobReviewStatus,
  type ReviewBasis,
  type ReviewExtraCost,
  type ReviewLabourLine,
  type ReviewSubcontractorCost,
} from './jobReview.model';

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' });
}

const SummaryTile: React.FC<{ label: string; value: string; detail?: string; color?: 'success' | 'danger' | 'neutral' }> = ({
  label,
  value,
  detail,
  color = 'neutral',
}) => (
  <Card variant="soft" color={color} sx={{ flex: '1 1 180px', p: 1.75, gap: 0.25 }}>
    <Typography level="body-xs" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>{label}</Typography>
    <Typography level="h4">{value}</Typography>
    {detail && <Typography level="body-xs">{detail}</Typography>}
  </Card>
);

const LegacyActuals: React.FC<{ rows: JobActuals[] }> = ({ rows }) => (
  <Card variant="outlined">
    <Typography level="title-md">Saved on the old Job Records page</Typography>
    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>Read-only. These figures were recorded against an invoice.</Typography>
    {rows.map((row) => (
      <Box key={row.id} sx={{ mt: 1 }}>
        <Divider sx={{ mb: 1 }} />
        <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 0.5 }}>Last updated {formatDate(row.updatedAt)}</Typography>
        <Table size="sm" sx={{ '& td:last-child, & th:last-child': { textAlign: 'right' } }}>
          <tbody>
            {row.actualLabour.map((item, index) => (
              <tr key={item.id || `labour-${index}`}>
                <td>{item.name || 'Labour'}</td>
                <td>{formatQty(Number(item.qty) || 0)} {item.unit || ''} @ {formatMoney(Number(item.unitPrice) || 0)}</td>
                <td>{formatMoney(Number(item.total) || 0)}</td>
              </tr>
            ))}
            {row.actualAdditionalCosts.map((item, index) => (
              <tr key={item.id || `extra-${index}`}>
                <td>{item.description || 'Additional cost'}</td>
                <td />
                <td>{formatMoney(Number(item.amount) || 0)}</td>
              </tr>
            ))}
            <tr>
              <td><Typography level="title-sm">Labour total</Typography></td>
              <td />
              <td><Typography level="title-sm">{formatMoney(row.actualLabourCost)}</Typography></td>
            </tr>
          </tbody>
        </Table>
        {row.notes && <Typography level="body-sm" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{row.notes}</Typography>}
      </Box>
    ))}
  </Card>
);

type Props = {
  projectId: string;
  onStatusChange?: (status: JobReviewStatus, complete: boolean) => void;
};

export const JobReviewPanel: React.FC<Props> = ({ projectId, onStatusChange }) => {
  const onStatusChangeRef = React.useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  const [detail, setDetail] = useState<JobReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [lines, setLines] = useState<ReviewLabourLine[]>([]);
  const [extras, setExtras] = useState<ReviewExtraCost[]>([]);
  const [subcontractors, setSubcontractors] = useState<ReviewSubcontractorCost[]>([]);
  const [notes, setNotes] = useState('');
  const [startedNew, setStartedNew] = useState(false);

  const applyDetail = useCallback((next: JobReviewDetail) => {
    setDetail(next);
    setLines(next.review ? next.labourLines : prefillActuals(next.labourLines));
    setExtras(next.review?.extraCosts || []);
    setSubcontractors(buildWorkerCosts(
      next.people,
      next.review?.subcontractorCosts || [],
      fittingDayRate(next.labourLines, next.rateComponents),
    ));
    setNotes(next.review?.notes || '');
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadJobReview(projectId)
      .then((next) => {
        if (cancelled) return;
        applyDetail(next);
        const nextStatus: JobReviewStatus = next.review?.status
          || ((next.legacy.length || 0) > 0 ? 'reviewed' : 'to_review');
        onStatusChangeRef.current?.(nextStatus, nextStatus === 'reviewed');
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load job review'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, applyDetail]);

  const sources = useMemo(() => detail?.sources || (detail?.quote ? [detail.quote] : []), [detail]);
  const components = detail?.rateComponents || null;
  const fittingFromWorkers = !!components && subcontractors.length > 0 && lines.some((line) => isCompanyFlatRate(line.name));
  const summary = useMemo(
    () => summariseReview(lines, extras, sources, { subcontractors, components, fittingFromWorkers }),
    [lines, extras, sources, subcontractors, components, fittingFromWorkers],
  );
  const review = detail?.review || null;
  const legacyOnly = !review && (detail?.legacy.length || 0) > 0;
  const status: JobReviewStatus = review?.status || (legacyOnly ? 'reviewed' : 'to_review');
  const editing = status === 'to_review' || (legacyOnly && startedNew);
  const showEditor = !legacyOnly || startedNew;
  const jobRef = sourceJobRef(detail?.quote) || null;
  const basis: ReviewBasis = detail?.basis || 'quote';

  const persist = async (nextStatus: JobReviewStatus) => {
    if (!detail) return;
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      await saveJobReview({
        projectId,
        quoteDocumentId: detail.quote?.id || null,
        labourLines: lines,
        extraCosts: extras,
        subcontractorCosts: subcontractors,
        notes,
        status: nextStatus,
        jobRef,
      });
      applyDetail(await loadJobReview(projectId));
      setStartedNew(false);
      setSaved(nextStatus === 'reviewed' ? 'Job Record complete.' : review?.status === 'reviewed' ? 'Reopened.' : 'Saved.');
      onStatusChangeRef.current?.(nextStatus, nextStatus === 'reviewed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const updateLine = (index: number, raw: string) => {
    const value = raw.trim() === '' ? null : Number(raw);
    if (value != null && (!Number.isFinite(value) || value < 0)) return;
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, actualQty: value } : line)));
  };

  const updateRate = (index: number, raw: string) => {
    const value = raw.trim() === '' ? null : Number(raw);
    if (value != null && (!Number.isFinite(value) || value < 0)) return;
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, actualRate: value } : line)));
  };

  const updateSubcontractor = (id: string, patch: Partial<ReviewSubcontractorCost>) => {
    setSubcontractors((prev) => prev.map((sub) => (sub.id === id ? { ...sub, ...patch } : sub)));
  };

  const parseAmount = (raw: string): number | null | undefined => {
    if (raw.trim() === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : undefined;
  };

  /** Days changed: with a day rate, the cost follows days × rate. */
  const updateWorkerDays = (row: ReviewSubcontractorCost, raw: string) => {
    const value = parseAmount(raw);
    if (value === undefined) return;
    const actualDays = value != null && value === row.days ? null : value;
    const days = value ?? row.days ?? 0;
    updateSubcontractor(row.id, {
      actualDays: row.days == null ? value : actualDays,
      ...(row.dayRate ? { amount: Math.round(days * row.dayRate * 100) / 100 } : {}),
    });
  };

  /** Day rate changed: the cost becomes days × rate. */
  const updateWorkerRate = (row: ReviewSubcontractorCost, raw: string) => {
    const value = parseAmount(raw);
    if (value === undefined) return;
    updateSubcontractor(row.id, {
      dayRate: value,
      ...(value ? { amount: Math.round(workerDays(row) * value * 100) / 100 } : {}),
    });
  };

  const updateExtra = (id: string, patch: Partial<ReviewExtraCost>) => {
    setExtras((prev) => prev.map((extra) => (extra.id === id ? { ...extra, ...patch } : extra)));
  };

  if (loading) {
    return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress size="sm" /></Stack>;
  }

  if (!detail) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert color="danger">{error || 'Job review not found.'}</Alert>
      </Box>
    );
  }

  const installDone = isInstallDone(detail.project);
  const canReview = canMarkReviewed(detail.project);
  const hasHours = lines.some((line) => line.unit === 'hour');
  const hasFlatRate = lines.some((line) => isCompanyFlatRate(line.name));
  const dayRate = fittingDayRate(lines, components);
  const quotedFitting = quotedFittingCost(lines, components);
  const workTotal = subcontractors.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const fittingVariance = Math.round((quotedFitting - workTotal) * 100) / 100;
  const fitting = fittingComparison(lines, components, subcontractors);
  // The flat rate prices one fitter's day (e.g. Tom); show them when they weren't on this job
  const pricedFitterAbsent = fittingFromWorkers && dayRate > 0 && !subcontractors.some((row) => row.covered && workerDays(row) > 0);

  return (
    <Stack gap={2}>
      <Box>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography level="title-md">Job Record</Typography>
          <Chip size="sm" variant="soft" color={status === 'reviewed' ? 'success' : 'warning'}>
            {status === 'reviewed' ? 'Complete' : 'Not complete'}
          </Chip>
        </Stack>
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          Sold versus implemented. Changes here do not rewrite the {basis === 'invoice' ? 'invoice' : 'quote'}. Complete this before the workspace can be marked complete.
          {' · '}
          {installDone
            ? `${detail.project.supply_only ? 'Delivered' : 'Installed'} ${formatDate(detail.installedOn)}`
            : detail.installedOn
              ? `${detail.project.supply_only ? 'Delivery booked to' : 'Install booked to'} ${formatDate(detail.installedOn)}`
              : `${detail.project.supply_only ? 'Delivery' : 'Install'} not booked`}
          {review?.status === 'reviewed' && ` · Completed ${formatDate(review.reviewedAt)}${detail.reviewerName ? ` by ${detail.reviewerName}` : ''}`}
        </Typography>
      </Box>

      {detail.tableMissing && <Alert color="warning">{JOB_REVIEWS_MISSING}</Alert>}
      {error && <Alert color="danger">{error}</Alert>}
      {saved && <Alert color="success">{saved}</Alert>}
      {status === 'to_review' && (
        <Alert color="neutral">
          Record what was actually used against every sold line. Mark complete when the evaluation is finished — that unlocks Complete on this workspace.
        </Alert>
      )}
      {!detail.quote && showEditor && (
        <Alert color="warning" sx={{ mb: 2 }}>This workspace has no accepted quote or works invoice, so there is nothing to compare.</Alert>
      )}

      {legacyOnly && !startedNew && (
        <Stack gap={2}>
          <LegacyActuals rows={detail.legacy} />
          <Box>
            <Button variant="outlined" onClick={() => setStartedNew(true)}>Start new review</Button>
          </Box>
        </Stack>
      )}

      {showEditor && (
        <Stack gap={2}>
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            {fittingFromWorkers && detail.crewSchedule ? (
              <SummaryTile
                label="Days on site"
                value={`${formatQty(detail.crewSchedule.siteDays)} of ${formatQty(summary.quotedDays)}`}
                detail={qtyVarianceLabel(summary.quotedDays, detail.crewSchedule.siteDays, 'day', basis)}
              />
            ) : (
              <SummaryTile
                label="Days on site"
                value={`${formatQty(summary.actualDays)} of ${formatQty(summary.quotedDays)}`}
                detail={qtyVarianceLabel(summary.quotedDays, summary.actualDays, 'day', basis)}
              />
            )}
            {hasHours && (
              <SummaryTile
                label="Hours"
                value={`${formatQty(summary.actualHours)} of ${formatQty(summary.quotedHours)}`}
                detail={qtyVarianceLabel(summary.quotedHours, summary.actualHours, 'hour', basis)}
              />
            )}
            <SummaryTile
              label="Sold (ex VAT)"
              value={formatMoney(summary.soldExVat)}
              detail="Fixed price"
            />
            <SummaryTile
              label="Actual cost"
              value={formatMoney(summary.actualCost)}
              detail={`Quoted ${formatMoney(summary.quotedCost)}`}
            />
            <SummaryTile
              label="Job profit"
              value={formatMoney(summary.actualProfit)}
              detail={`Planned ${formatMoney(summary.plannedProfit)} · ${summary.variance === 0
                ? 'on plan'
                : `${formatMoney(Math.abs(summary.variance))} ${summary.variance < 0 ? 'below' : 'above'} plan`}`}
              color={varianceColor(summary.variance)}
            />
          </Stack>

          <Card variant="outlined" sx={{ p: 0, overflow: 'auto' }}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography level="title-md">Who did the work</Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                Everyone on the job cards. Days and day rate work out each person&apos;s cost, before VAT; change the cost if their bill is different.
              </Typography>
            </Box>
            <Table sx={{ minWidth: 640, mt: 1, '& th, & td': { px: 2 }, '& td': { verticalAlign: 'middle' } }}>
              <thead>
                <tr>
                  <th>Person</th>
                  <th style={{ width: 110, textAlign: editing ? 'left' : 'right' }}>Days</th>
                  <th style={{ width: 150 }}>Day rate</th>
                  <th style={{ width: 160 }}>Cost</th>
                  {editing && <th style={{ width: 48 }} aria-label="Remove" />}
                </tr>
              </thead>
              <tbody>
                {pricedFitterAbsent && (
                  <tr>
                    <td>
                      <Typography level="body-sm" fontWeight="md" sx={{ color: 'neutral.500' }}>
                        {detail.pricedFitterName || 'In-house fitter'}
                      </Typography>
                      <Chip size="sm" variant="soft" color="warning">Priced in the quote, not on this job</Chip>
                    </td>
                    <td style={{ textAlign: editing ? 'left' : 'right' }}><Typography level="body-sm" sx={{ color: 'neutral.500' }}>0</Typography></td>
                    <td><Typography level="body-sm" sx={{ color: 'neutral.500' }}>{formatMoney(dayRate)}/day</Typography></td>
                    <td><Typography level="body-sm" sx={{ color: 'neutral.500' }}>{formatMoney(0)}</Typography></td>
                    {editing && <td />}
                  </tr>
                )}
                {subcontractors.length === 0 && (
                  <tr>
                    <td colSpan={editing ? 5 : 4}>
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>Nobody on the job cards yet.</Typography>
                    </td>
                  </tr>
                )}
                {subcontractors.map((row) => {
                  const fromCards = row.days != null;
                  const days = workerDays(row);
                  const daysChanged = fromCards && row.actualDays != null && row.actualDays !== row.days;
                  return (
                    <tr key={row.id}>
                      <td>
                        {editing && !fromCards ? (
                          <Select
                            size="sm"
                            placeholder="Choose from the directory"
                            value={row.accountId || null}
                            onChange={(_, value) => {
                              const account = detail.subcontractorAccounts.find((option) => option.accountId === value);
                              if (account) updateSubcontractor(row.id, { accountId: account.accountId, name: account.name });
                            }}
                            renderValue={(option) => (option ? option.label : row.name || null)}
                            slotProps={{ listbox: { sx: { maxHeight: 260, overflow: 'auto' } } }}
                          >
                            {detail.subcontractorAccounts
                              .filter((account) => account.accountId === row.accountId
                                || !subcontractors.some((other) => other.accountId === account.accountId))
                              .map((account) => (
                                <Option key={account.accountId} value={account.accountId}>{account.name}</Option>
                              ))}
                          </Select>
                        ) : (
                          <Typography level="body-sm" fontWeight="md">{row.name || 'Unnamed'}</Typography>
                        )}
                        {row.covered && dayRate > 0 && (
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Priced in the quote at {formatMoney(dayRate)}/day</Typography>
                        )}
                      </td>
                      <td style={{ textAlign: editing ? 'left' : 'right' }}>
                        {editing ? (
                          <Input
                            size="sm"
                            type="number"
                            value={row.actualDays ?? row.days ?? ''}
                            placeholder="0"
                            onChange={(event) => updateWorkerDays(row, event.target.value)}
                            slotProps={{ input: { min: 0, step: 0.5, 'aria-label': `Days for ${row.name || 'person'}` } }}
                          />
                        ) : (
                          formatQty(days)
                        )}
                        {daysChanged && (
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Job cards: {formatQty(row.days || 0)}</Typography>
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <Input
                            size="sm"
                            type="number"
                            startDecorator="£"
                            value={row.dayRate ?? ''}
                            placeholder="0"
                            onChange={(event) => updateWorkerRate(row, event.target.value)}
                            slotProps={{ input: { min: 0, step: 0.01, 'aria-label': `Day rate for ${row.name || 'person'}` } }}
                          />
                        ) : (
                          row.dayRate ? `${formatMoney(row.dayRate)}/day` : '—'
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <Input
                            size="sm"
                            type="number"
                            startDecorator="£"
                            value={row.amount || ''}
                            placeholder="0"
                            onChange={(event) => updateSubcontractor(row.id, { amount: Number(event.target.value) || 0 })}
                            slotProps={{ input: { min: 0, step: 0.01, 'aria-label': `Cost for ${row.name || 'person'}` } }}
                          />
                        ) : (
                          formatMoney(row.amount)
                        )}
                        {!!row.dayRate && Math.abs(row.amount - Math.round(days * row.dayRate * 100) / 100) > 0.005 && (
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                            Not days × rate ({formatMoney(Math.round(days * row.dayRate * 100) / 100)})
                          </Typography>
                        )}
                      </td>
                      {editing && (
                        <td>
                          {!fromCards && (
                            <IconButton
                              size="sm"
                              variant="plain"
                              color="neutral"
                              aria-label="Remove"
                              onClick={() => setSubcontractors((prev) => prev.filter((other) => other.id !== row.id))}
                            >
                              <IconWrapper icon={FaTimes} />
                            </IconButton>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {subcontractors.length > 0 && (
                  <tr>
                    <td>
                      <Typography level="title-sm">Total</Typography>
                      {detail.crewSchedule && (
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{formatQty(detail.crewSchedule.siteDays)} days on site</Typography>
                      )}
                    </td>
                    <td style={{ textAlign: editing ? 'left' : 'right' }}>
                      <Typography level="title-sm">{formatQty(fitting.actualDays)}</Typography>
                    </td>
                    <td />
                    <td colSpan={editing ? 2 : 1}>
                      <Typography level="title-sm">{formatMoney(workTotal)}</Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            {fittingFromWorkers && (
              <Box sx={{ mx: 2, my: 1.5, p: 1.5, borderRadius: 'sm', bgcolor: 'background.level1' }}>
                <Typography level="title-sm">Fitting: priced against actual</Typography>
                <Typography level="body-sm">
                  The quote priced {formatQty(fitting.pricedDays)} day{fitting.pricedDays === 1 ? '' : 's'} at {formatMoney(fitting.pricedRate)}/day
                  {detail.pricedFitterName ? ` (${detail.pricedFitterName}'s rate)` : ''} = <b>{formatMoney(fitting.pricedCost)}</b>.
                  {' '}Actually {formatQty(fitting.actualDays)} day{fitting.actualDays === 1 ? '' : 's'} costing <b>{formatMoney(fitting.actualCost)}</b>.
                </Typography>
                <Typography level="body-sm" fontWeight="lg" color={fitting.over > 0 ? 'danger' : fitting.over < 0 ? 'success' : 'neutral'}>
                  {fitting.over === 0
                    ? 'On the priced amount.'
                    : `${formatMoney(Math.abs(fitting.over))} ${fitting.over > 0 ? 'more' : 'less'} than priced.`}
                </Typography>
              </Box>
            )}
            {editing && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Button
                  size="sm"
                  variant="plain"
                  onClick={() => setSubcontractors((prev) => [...prev, { id: crypto.randomUUID(), name: '', accountId: null, amount: 0 }])}
                >
                  Add someone not on a job card
                </Button>
              </Box>
            )}
          </Card>

          {hasFlatRate && !components && (
            <Alert color="warning">
              The Company Flat Rate split isn&apos;t set up, so the whole flat rate is treated as a cost.
              Apply migration 20261018130000-company-rate-components.sql.
            </Alert>
          )}

          {lines.length === 0 ? (
            <Card variant="outlined">
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                {basis === 'invoice' ? 'No lines on the invoice.' : 'No lines on the accepted quote.'}
              </Typography>
            </Card>
          ) : groupReviewLines(lines).map((group) => (
            <Card key={group.kind} variant="outlined" sx={{ p: 0, overflow: 'auto' }}>
              <Box sx={{ px: 2, pt: 2 }}>
                <Typography level="title-md">{group.label}</Typography>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  Sold on the {basis === 'invoice' ? 'invoice' : 'quote'}. Enter what was actually used and what it actually cost. This does not change the {basis === 'invoice' ? 'invoice' : 'quote'}.
                </Typography>
              </Box>
              <Table sx={{ minWidth: 820, mt: 1, '& th, & td': { px: 2 }, '& td': { verticalAlign: 'middle' } }}>
                <thead>
                  <tr>
                    <th>Line</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Sold rate</th>
                    <th style={{ width: 100, textAlign: 'right' }}>Sold</th>
                    <th style={{ width: 140 }}>Actual</th>
                    <th style={{ width: 140 }}>Actual rate</th>
                    <th style={{ width: 160, textAlign: 'right' }}>Cost difference</th>
                  </tr>
                </thead>
                <tbody>
                  {group.lines.map((line) => {
                    const index = lines.indexOf(line);
                    const actual = line.actualQty ?? line.quotedQty;
                    const cost = reviewLineCost(line, sources, components);
                    const variance = cost.quoted - cost.actual;
                    const soldUnitRate = cost.split ? cost.split.fitting : line.rate;
                    const actualRate = line.actualRate ?? soldUnitRate;
                    return (
                      <tr key={line.lineId || `${group.kind}-${line.name}-${index}`}>
                        <td>
                          <Typography level="body-sm" fontWeight="md">{line.name}</Typography>
                          {!line.lineId && <Typography level="body-xs" sx={{ color: 'neutral.500' }}>No longer on the {basis === 'invoice' ? 'invoice' : 'quote'}</Typography>}
                          {cost.split && (
                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                              Per day: directors {formatMoney(cost.split.directors)} + running costs {formatMoney(cost.split.overhead)}, kept as quoted ({formatMoney(cost.companyShare)})
                              {fittingFromWorkers
                                ? `, + fitting ${formatMoney(cost.split.fitting)}, costed under Who did the work.`
                                : ` + fitting ${formatMoney(cost.split.fitting)}. Actual is in-house fitting only.`}
                            </Typography>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {formatMoney(line.rate)}
                          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>per {unitLabel(line.unit, 1)}</Typography>
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatQty(line.quotedQty)} {unitLabel(line.unit, line.quotedQty)}</td>
                        <td>
                          {cost.split && fittingFromWorkers ? (
                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>See Who did the work</Typography>
                          ) : editing ? (
                            <Input
                              size="sm"
                              type="number"
                              value={line.actualQty ?? ''}
                              onChange={(event) => updateLine(index, event.target.value)}
                              endDecorator={<Typography level="body-xs">{unitLabel(line.unit, 2)}</Typography>}
                              slotProps={{ input: { min: 0, step: line.unit === 'hour' ? 0.25 : 0.5, 'aria-label': `Actual ${line.name}` } }}
                            />
                          ) : (
                            `${formatQty(actual)} ${unitLabel(line.unit, actual)}`
                          )}
                        </td>
                        <td>
                          {cost.split && fittingFromWorkers ? null : editing ? (
                            <Input
                              size="sm"
                              type="number"
                              startDecorator="£"
                              value={line.actualRate ?? ''}
                              placeholder={String(soldUnitRate)}
                              onChange={(event) => updateRate(index, event.target.value)}
                              slotProps={{ input: { min: 0, step: 0.01, 'aria-label': `Actual rate ${line.name}` } }}
                            />
                          ) : (
                            formatMoney(actualRate)
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {cost.split && fittingFromWorkers ? (
                            <Typography level="body-sm" color={varianceColor(fittingVariance)}>
                              {moneyVarianceLabel(fittingVariance, basis)}
                            </Typography>
                          ) : (
                            <Typography level="body-sm" color={varianceColor(variance)}>{moneyVarianceLabel(variance, basis)}</Typography>
                          )}
                          {!(cost.split && fittingFromWorkers) && (
                            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{qtyVarianceLabel(line.quotedQty, actual, line.unit, basis)}</Typography>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card>
          ))}

          <Card variant="outlined">
            <Typography level="title-md">Extra costs</Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              Anything the {basis === 'invoice' ? 'invoice' : 'quote'} did not cover. This stays on the Job Record only.
            </Typography>
            <Stack gap={1} sx={{ mt: 1 }}>
              {extras.length === 0 && !editing && <Typography level="body-sm">None.</Typography>}
              {extras.map((extra) => (
                editing ? (
                  <Stack key={extra.id} direction="row" gap={1} alignItems="center">
                    <Input
                      size="sm"
                      placeholder="Description"
                      value={extra.description}
                      onChange={(event) => updateExtra(extra.id, { description: event.target.value })}
                      sx={{ flex: 1 }}
                    />
                    <Input
                      size="sm"
                      type="number"
                      startDecorator="£"
                      value={extra.amount || ''}
                      onChange={(event) => updateExtra(extra.id, { amount: Number(event.target.value) || 0 })}
                      slotProps={{ input: { min: 0, step: 0.01, 'aria-label': 'Amount' } }}
                      sx={{ width: 140 }}
                    />
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      aria-label="Remove extra cost"
                      onClick={() => setExtras((prev) => prev.filter((row) => row.id !== extra.id))}
                    >
                      <IconWrapper icon={FaTimes} />
                    </IconButton>
                  </Stack>
                ) : (
                  <Stack key={extra.id} direction="row" justifyContent="space-between">
                    <Typography level="body-sm">{extra.description || 'Extra cost'}</Typography>
                    <Typography level="body-sm">{formatMoney(extra.amount)}</Typography>
                  </Stack>
                )
              ))}
              {editing && (
                <Box>
                  <Button
                    size="sm"
                    variant="plain"
                    onClick={() => setExtras((prev) => [...prev, { id: crypto.randomUUID(), description: '', amount: 0 }])}
                  >
                    Add extra cost
                  </Button>
                </Box>
              )}
            </Stack>
          </Card>

          <Card variant="outlined">
            <Typography level="title-md">Note</Typography>
            {editing ? (
              <Textarea
                minRows={3}
                placeholder="Why did the job run over or finish early?"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            ) : (
              <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{notes || 'No note.'}</Typography>
            )}
          </Card>

          {error && <Alert color="danger">{error}</Alert>}
          {saved && <Alert color="success">{saved}</Alert>}
          <Stack direction="row" justifyContent="flex-end" gap={1}>
            {editing ? (
              <>
                {legacyOnly && (
                  <Button variant="plain" color="neutral" disabled={saving} onClick={() => setStartedNew(false)}>Cancel</Button>
                )}
                <Button variant="outlined" loading={saving} onClick={() => void persist('to_review')}>Save</Button>
                <Button loading={saving} disabled={!canReview} onClick={() => void persist('reviewed')}>Mark complete</Button>
              </>
            ) : (
              <Button variant="outlined" loading={saving} onClick={() => void persist('to_review')}>Reopen</Button>
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default JobReviewPanel;
